/**
 * SessionSettings - the per-chat output-language control.
 *
 * There is deliberately no jurisdiction selector. The index tree below this
 * panel lists indexes grouped BY jurisdiction, so ticking a group already
 * says "search Norway" - a second control saying the same thing could
 * disagree with the tree, and did.
 *
 * Every value shown is the backend's EFFECTIVE value, with its `*_source`
 * telling us which layer answered. That drives the only two states the user
 * needs to tell apart:
 *
 *   session      → "Set for this chat"  (+ a reset affordance)
 *   anything else→ "Inherited from your settings"
 *
 * Resetting sends `null`, which clears the override server-side - it does not
 * write the inherited value back as a new override.
 */

import React, { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import SearchInput from '../shared/TailwindComponents/SearchInput';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import useDebouncedValue from './useDebouncedValue';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiArrowCounterClockwiseLight, PiCaretDownLight, PiCheckLight } from 'react-icons/pi';
import { SettingsSource } from '../../store/actions/ChatAction';

/* ------------------------------------------------------------------ */
/*  Provenance                                                         */
/* ------------------------------------------------------------------ */

/** Only a `session` value is an override; everything else is inherited. */
export function isOverridden(source: SettingsSource | undefined): boolean {
  return source === 'session';
}

/** Untranslated hint for a field's provenance. */
export function provenanceHint(source: SettingsSource | undefined): string {
  switch (source) {
    case 'session':
      return 'Set for this chat';
    case 'ai_settings':
      return 'Inherited from your settings';
    case 'default':
      return 'Platform default';
    default:
      return 'Not set';
  }
}

const ProvenanceRow: React.FC<{
  source: SettingsSource | undefined;
  onReset?: () => void;
  disabled?: boolean;
}> = ({ source, onReset, disabled }) => {
  const { t } = useTranslation();
  const overridden = isOverridden(source);

  return (
    <div className="mt-1 flex items-center gap-2">
      <span
        data-testid="provenance-hint"
        data-source={source || 'none'}
        className="inline-flex items-center gap-1 text-[10px] text-fg-muted"
      >
        {overridden && <span className="w-1 h-1 rounded-full bg-champagne" aria-hidden />}
        {t(provenanceHint(source))}
      </span>
      {overridden && onReset && (
        <button
          type="button"
          onClick={onReset}
          disabled={disabled}
          data-testid="settings-reset"
          className="inline-flex items-center gap-1 text-[10px] text-fg-muted
                     hover:text-champagne transition-colors duration-200 ease-slide
                     disabled:opacity-50"
        >
          <PiArrowCounterClockwiseLight className="w-3 h-3" aria-hidden />
          {t('Reset to default')}
        </button>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  A small glass select, matching the composer's intent chip          */
/* ------------------------------------------------------------------ */

/** Below this many options a filter is noise, not help. */
export const SEARCHABLE_FROM = 8;

export interface SettingOption {
  value: string;
  label: string;
}

const SettingMenu: React.FC<{
  value: string | null;
  options: SettingOption[];
  placeholder: string;
  disabled?: boolean;
  testId: string;
  onSelect: (value: string) => void;
  /** No options to choose from - say so instead of an inert control. */
  emptyHint: string;
}> = ({ value, options, placeholder, disabled, testId, onSelect, emptyHint }) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  // The field stays instant; only the filter waits for typing to settle.
  const settled = useDebouncedValue(query, 150);

  const visible = React.useMemo(() => {
    const needle = settled.trim().toLowerCase();
    if (!needle) return options;
    return options.filter(
      (o) => o.label.toLowerCase().includes(needle) || o.value.toLowerCase().includes(needle)
    );
  }, [options, settled]);
  // The stored value may be a code ("en") or a name ("english"), depending on
  // which layer supplied it, so match on either.
  const needle = (value || '').toLowerCase();
  const current = options.find(
    (o) => o.value.toLowerCase() === needle || o.label.toLowerCase() === needle
  );

  // Unavailable list: still show whatever value is in force, never a blank
  // control, and never block the rest of the panel.
  if (options.length === 0) {
    return (
      <div
        data-testid={testId}
        data-empty="true"
        className="px-2.5 py-1.5 rounded-lg text-xs bg-surface-sunken
                   text-fg-muted border border-hairline-soft"
      >
        {value || t(emptyHint)}
      </div>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-start w-full">
      <Menu.Button
        disabled={disabled}
        data-testid={testId}
        className={clsx(
          'inline-flex items-center justify-between gap-1 w-full px-2.5 py-1.5 rounded-lg text-xs',
          'bg-surface-sunken text-fg-body border border-hairline-soft',
          'hover:border-champagne/50 transition-colors duration-200 ease-slide',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        <span className="truncate">{current?.label || value || t(placeholder)}</span>
        <PiCaretDownLight className="w-3 h-3 text-fg-muted flex-shrink-0" aria-hidden />
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-pop duration-150"
        enterFrom="opacity-0 -translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 -translate-y-1"
      >
        <Menu.Items
          // The search is pinned and the OPTIONS scroll, so the field stays
          // reachable in a long list. Scrolling the whole popup put the input
          // out of view the moment the user scrolled to look for a match.
          className="absolute start-0 end-0 mt-1 z-30 max-h-64 flex flex-col
                     overflow-hidden glass rounded-lg shadow-sm p-1 focus:outline-none"
        >
          {/* Search first. Only rendered when the list is long enough to be
              worth scanning - a three-item list needs no filter. */}
          {options.length > SEARCHABLE_FROM && (
            <div
              className="relative p-1 flex-shrink-0"
              data-testid={`${testId}-search-bar`}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <SearchInput
                size="small"
                autoFocus
                value={query}
                onChange={setQuery}
                placeholder={t('Search')}
                aria-label={t('Search')}
                data-testid={`${testId}-search`}
              />
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto" data-testid={`${testId}-options`}>
          {visible.length === 0 && (
            <p className="px-2.5 py-2 text-xs text-fg-muted text-center"
               data-testid={`${testId}-no-matches`}>
              {t('No matches')}
            </p>
          )}

          {visible.map((option) => (
            <Menu.Item key={option.value}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onSelect(option.value)}
                  className={clsx(
                    'flex items-center justify-between w-full px-2.5 py-1.5 rounded-md text-xs text-start',
                    active ? 'bg-surface-sunken text-fg-strong' : 'text-fg-body'
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {(option.value.toLowerCase() === needle || option.label.toLowerCase() === needle) && (
                    <PiCheckLight className="w-3.5 h-3.5 text-champagne flex-shrink-0" aria-hidden />
                  )}
                </button>
              )}
            </Menu.Item>
          ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

/* ------------------------------------------------------------------ */
/*  The panel                                                          */
/* ------------------------------------------------------------------ */

interface Props {
  outputLanguage: string;
  outputLanguageSource: SettingsSource;
  languageOptions: SettingOption[];
  onOutputLanguageChange: (language: string) => void;
  onOutputLanguageReset: () => void;

  /** No chat open yet - settings apply from the first message. */
  disabled?: boolean;
  /** The preference/settings read is still in flight. */
  isLoading?: boolean;
  isSaving?: boolean;
}

const SessionSettings: React.FC<Props> = ({
  outputLanguage,
  outputLanguageSource,
  languageOptions,
  onOutputLanguageChange,
  onOutputLanguageReset,
  disabled = false,
  isLoading = false,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3 font-sans" data-testid="session-settings">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted mb-1">
          {t('Answer language')}
        </p>
        {/* A blank control that pops in a value a beat later reads as "you
            have no preference". Hold the shape until the read settles. */}
        {isLoading ? (
          <Skeleton variant="option" rows={1} inset="none" />
        ) : (
          <>
            <SettingMenu
              testId="language-select"
              value={outputLanguage}
              options={languageOptions}
              placeholder={t('Choose a language')}
              emptyHint="Language list unavailable"
              disabled={isSaving}
              onSelect={onOutputLanguageChange}
            />
            <ProvenanceRow
              source={outputLanguageSource}
              onReset={onOutputLanguageReset}
              disabled={isSaving}
            />
          </>
        )}
      </div>

      {disabled && (
        <p className="text-[10px] text-fg-muted">
          {t('Applied from your first message.')}
        </p>
      )}
    </div>
  );
};

export default SessionSettings;
